import * as React from 'react'
import { PLANT_PLC_NODE_QUERY_PARAM, plantHref } from '@atbash-labs/plantview-links'
import { usePlantSessionPaint, type PlantPlcTreeNode } from '@axilon-apps/domains-ontology-ui-state'
import {
  PlantTreeNav,
  type PlantTreeNavGhost,
  type PlantTreeNavGroup,
  type PlantTreeNavNode
} from './PlantTreeNav'
import { collectUnplacedGhosts } from './session-mark-ui'
import { plcIconFor } from './plc-icon'

/** Child kinds that ghost under a Controller in the PLC tree. */
const CONTROLLER_CHILD_LABELS = new Set(['Program', 'AOI', 'PLCDataType'])
/** Child kinds that ghost under a Program in the PLC tree. */
const PROGRAM_CHILD_LABELS = new Set(['Routine'])

/**
 * PLC tab adapter over {@link PlantTreeNav}. Renders the global
 * Controller / Program / Routine / AOI / PLCDataType tree from the flat
 * list returned by `useGetPlantPlcTreeQuery`. Programs, AOIs, and data
 * types all hang directly off their owning Controller but land in
 * labeled groups ("Programs" / "AOIs" / "Data types") so users can find
 * each family without scrolling past the others; Routines nest under
 * their Program as a plain unlabeled group.
 *
 * Controllers start collapsed so the rail stays scannable on large
 * fleets — the shared rail's ancestor auto-expand still reveals a
 * deep-linked `?node=<id>` selection.
 *
 * When a session is painting, rows carry word-pill marks and deleted
 * children render as ghost rows under their parent (or at the root when
 * unplaced). Parent rollup pills stay off the rail — expand to see
 * each child mark in place.
 */
export interface PlcTreeNavProps {
  nodes: PlantPlcTreeNode[]
  selectedId?: string
  isLoading?: boolean
  className?: string
}

function collectGhosts(
  containerId: string,
  labels: ReadonlySet<string>,
  paintOn: boolean,
  ghostsByContainerId: ReturnType<typeof usePlantSessionPaint>['ghostsByContainerId'],
  markFor: ReturnType<typeof usePlantSessionPaint>['markFor']
): PlantTreeNavGhost[] | undefined {
  if (!paintOn) return undefined
  const ghosts: PlantTreeNavGhost[] = []
  for (const ghost of ghostsByContainerId.get(containerId) ?? []) {
    if (!ghost.labels.some((label) => labels.has(label))) continue
    const view = markFor(ghost.id)
    if (view !== 'deleted' && view !== 'dimmed') continue
    ghosts.push({ id: ghost.id, name: ghost.name ?? ghost.id, view })
  }
  return ghosts.length > 0 ? ghosts : undefined
}

function buildNavNodes(
  nodes: PlantPlcTreeNode[],
  paintOn: boolean,
  markFor: ReturnType<typeof usePlantSessionPaint>['markFor'],
  ghostsByContainerId: ReturnType<typeof usePlantSessionPaint>['ghostsByContainerId']
): PlantTreeNavNode[] {
  const toNavNode = (
    node: PlantPlcTreeNode,
    groups: PlantTreeNavGroup[] = [],
    ghosts?: PlantTreeNavGhost[]
  ): PlantTreeNavNode => ({
    id: node.id,
    href: plantHref(node.id, node.kind, node),
    label: node.name,
    icon: plcIconFor(node.kind),
    count: node.tagCount > 0 ? node.tagCount : undefined,
    mark: paintOn ? markFor(node.id) : undefined,
    ghosts,
    groups
  })

  const routinesByProgram = new Map<string, PlantPlcTreeNode[]>()
  for (const node of nodes) {
    if (node.kind !== 'Routine' || !node.parentId) continue
    const siblings = routinesByProgram.get(node.parentId)
    if (siblings) siblings.push(node)
    else routinesByProgram.set(node.parentId, [node])
  }

  const toProgramNode = (program: PlantPlcTreeNode): PlantTreeNavNode => {
    const routines = routinesByProgram.get(program.id) ?? []
    return toNavNode(
      program,
      [{ nodes: routines.map((routine) => toNavNode(routine)) }],
      collectGhosts(program.id, PROGRAM_CHILD_LABELS, paintOn, ghostsByContainerId, markFor)
    )
  }

  const roots: PlantTreeNavNode[] = []
  for (const node of nodes) {
    if (node.kind !== 'Controller') continue
    const programs: PlantTreeNavNode[] = []
    const aois: PlantTreeNavNode[] = []
    const dataTypes: PlantTreeNavNode[] = []
    for (const child of nodes) {
      if (child.parentId !== node.id) continue
      if (child.kind === 'Program') programs.push(toProgramNode(child))
      else if (child.kind === 'AOI') aois.push(toNavNode(child))
      else if (child.kind === 'PLCDataType') dataTypes.push(toNavNode(child))
    }
    roots.push(
      toNavNode(
        node,
        [
          { label: 'Programs', nodes: programs },
          { label: 'AOIs', nodes: aois },
          { label: 'Data types', nodes: dataTypes }
        ],
        collectGhosts(node.id, CONTROLLER_CHILD_LABELS, paintOn, ghostsByContainerId, markFor)
      )
    )
  }
  return roots
}

export function PlcTreeNav({
  nodes,
  selectedId,
  isLoading,
  className
}: PlcTreeNavProps): React.ReactElement {
  const { active, filter, markFor, ghostsByContainerId, markById } = usePlantSessionPaint()
  const paintOn = active && filter.enabled

  const navNodes = React.useMemo(
    () => buildNavNodes(nodes, paintOn, markFor, ghostsByContainerId),
    [ghostsByContainerId, markFor, nodes, paintOn]
  )

  const rootGhosts = React.useMemo((): PlantTreeNavGhost[] | undefined => {
    if (!paintOn) return undefined
    const liveIds = new Set(nodes.map((node) => node.id))
    const unplaced = collectUnplacedGhosts(markById, ghostsByContainerId, markFor, {
      label: 'Controller',
      liveIds
    })
    if (unplaced.length === 0) return undefined
    return unplaced.map((ghost) => ({
      id: ghost.id,
      name: ghost.name ?? ghost.id,
      view: ghost.view
    }))
  }, [ghostsByContainerId, markById, markFor, nodes, paintOn])

  return (
    <PlantTreeNav
      heading="PLC tree"
      nodes={navNodes}
      selectParam={PLANT_PLC_NODE_QUERY_PARAM}
      selectedId={selectedId}
      isLoading={isLoading}
      loadingText="Loading controllers…"
      emptyTitle="No controllers"
      emptyHint="Upload an L5X, ACD, TIA, or CODESYS export from the Ingest tab to populate the PLC tree."
      defaultExpanded={false}
      rootGhosts={rootGhosts}
      className={className}
    />
  )
}
